import React, { useState } from 'react';
import { decryptJSON } from '../utils/encryption';
import { saveAs } from 'file-saver';
import { getJsonFromIPFS } from '../utils/ipfsClient';

function DecryptionForm({ onClose }) {
  const [encryptedData, setEncryptedData] = useState('');
  const [ipfsUrl, setIpfsUrl] = useState('');
  const [key, setKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [decryptedData, setDecryptedData] = useState(null);
  const [activeTab, setActiveTab] = useState('ipfs');

  const extractCIDFromURL = (url) => {
    // Example URL: https://ipfs.filebase.io/ipfs/QmYwAPJzv5CZsnA625s3Xf2nemtYgPpHdWEz79ojWnPbdG
    if (!url) return null;
    
    const ipfsMatch = url.match(/\/ipfs\/([a-zA-Z0-9]+)/);
    if (ipfsMatch && ipfsMatch[1]) {
      return ipfsMatch[1];
    }
    
    // If the input is just a CID
    if (/^[a-zA-Z0-9]+$/.test(url)) {
      return url;
    }
    
    return null;
  };

  const handleFetchFromIPFS = async () => {
    try {
      setLoading(true);
      setError('');
      setDecryptedData(null);
      
      const cid = extractCIDFromURL(ipfsUrl);
      if (!cid) {
        throw new Error('Invalid IPFS URL or CID. Please provide a valid URL containing /ipfs/CID or just the CID.');
      }
      
      const { data } = await getJsonFromIPFS(cid);
      setEncryptedData(data);
      setActiveTab('paste');
      
    } catch (err) {
      setError(`Failed to fetch from IPFS: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDecrypt = async () => {
    try {
      setLoading(true);
      setError('');
      setDecryptedData(null);
      
      if (!encryptedData) {
        throw new Error('No encrypted data provided');
      }
      
      if (!key || key.length < 4) {
        throw new Error('The key must be at least 4 characters long');
      }

      // Check for common paste errors
      if (encryptedData.startsWith('"') && encryptedData.endsWith('"')) {
        console.warn('Detected quotes around encrypted data - attempting to remove them');
        // Notify user but continue with corrected data
        setError('Warning: Quotes detected around data. Attempting to correct automatically.');
      }

      let dataToDecrypt = encryptedData.trim();
      
      // Remove surrounding quotes if they exist
      if (dataToDecrypt.startsWith('"') && dataToDecrypt.endsWith('"')) {
        dataToDecrypt = dataToDecrypt.substring(1, dataToDecrypt.length - 1);
      }
      
      // Check if the input is JSON and try to extract the encrypted string
      if (typeof encryptedData === 'string' && encryptedData.trim().startsWith('{')) {
        try {
          const jsonData = JSON.parse(encryptedData);
          // If there's a data field, use that as the encrypted content
          if (jsonData.data) {
            dataToDecrypt = jsonData.data;
          }
        } catch (e) {
          // If it's not valid JSON, use as is
          console.log('Not JSON or unable to parse:', e);
        }
      }
      
      const result = decryptJSON(dataToDecrypt, key);
      
      if (!result.success) {
        throw new Error(result.error || 'Decryption failed. Check your key or encrypted data.');
      }
      
      setDecryptedData(result.data);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDecrypted = () => {
    if (!decryptedData) return;
    
    const blob = new Blob(
      [JSON.stringify(decryptedData, null, 2)], 
      { type: 'application/json' }
    );
    saveAs(blob, 'decrypted_data.json');
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Decrypt Data</h2>
      </div>
      <div className="card-body">
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'ipfs' ? 'active' : ''}`}
            onClick={() => setActiveTab('ipfs')}
          >
            Fetch from IPFS
          </button>
          <button 
            className={`tab-button ${activeTab === 'paste' ? 'active' : ''}`}
            onClick={() => setActiveTab('paste')}
          >
            Paste Encrypted Data
          </button>
        </div>
        
        {activeTab === 'ipfs' && (
          <div className="tab-content">
            <div className="form-group">
              <label className="form-label">IPFS URL or CID:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={ipfsUrl}
                  onChange={(e) => setIpfsUrl(e.target.value)}
                  className="form-control"
                  placeholder="https://ipfs.filebase.io/ipfs/CID or just the CID"
                  style={{
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    paddingRight: '10px'
                  }}
                />
              </div>
              <div style={{ 
                marginTop: '0.5rem',
                display: ipfsUrl.length > 20 ? 'block' : 'none',
                padding: '0.5rem',
                backgroundColor: 'var(--gray-100)',
                borderRadius: '0.25rem',
                fontSize: '0.85rem',
                fontFamily: 'monospace',
                overflowX: 'auto',
                whiteSpace: 'nowrap'
              }}>
                {ipfsUrl}
              </div>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleFetchFromIPFS}
              disabled={loading || !ipfsUrl}
            >
              {loading ? 'Fetching...' : 'Fetch from IPFS'}
            </button>
          </div>
        )}
        
        {activeTab === 'paste' && (
          <div className="tab-content">
            <div className="form-group">
              <label className="form-label">Encrypted Data:</label>
              <textarea
                value={encryptedData}
                onChange={(e) => setEncryptedData(e.target.value)}
                className="form-control textarea"
                placeholder="Paste the encrypted data here"
                rows={5}
              />
              <div style={{ 
                marginTop: '0.5rem', 
                padding: '0.75rem',
                backgroundColor: 'var(--warning-light)',
                borderLeft: '4px solid var(--warning)',
                borderRadius: '0 0.25rem 0.25rem 0',
                fontSize: '0.85rem'
              }}>
                <strong>Important:</strong> When pasting encrypted data, ensure that:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem', marginBottom: '0' }}>
                  <li>All content is selected and copied properly</li>
                  <li style={{ color: 'var(--danger)', fontWeight: 'bold' }}>No extra quotes, spaces, or characters are included</li>
                  <li>The data appears as a single unbroken string</li>
                  <li>Special characters are preserved during copy/paste</li>
                </ul>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Decryption Key:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className="form-control"
                  placeholder="Enter the key used for encryption"
                  style={{ 
                    paddingRight: '40px',
                    color: showPassword ? 'var(--gray-600)' : 'inherit' 
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    color: 'var(--gray-500)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '30px',
                    height: '30px'
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                  )}
                </button>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.3rem' }}>
                Toggle visibility icon to check key accuracy before decrypting
              </div>
            </div>
            
            <button 
              className="btn btn-primary"
              onClick={handleDecrypt}
              disabled={loading || !encryptedData || !key}
            >
              {loading ? 'Decrypting...' : 'Decrypt Data'}
            </button>
          </div>
        )}
        
        {error && (
          <div className="error-message mt-3">
            {error}
          </div>
        )}
        
        {decryptedData && (
          <div className="decrypted-result mt-4">
            <h3>Decrypted Data:</h3>
            <div className="json-preview">
              <pre>
                {JSON.stringify(decryptedData, null, 2)}
              </pre>
            </div>
            <div className="button-group mt-3">
              <button 
                className="btn btn-success"
                onClick={handleDownloadDecrypted}
              >
                Download Decrypted JSON
              </button>
            </div>
          </div>
        )}
        
        <div className="button-group mt-4">
          <button 
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default DecryptionForm;